import React from 'react';

import { Box, Typography, IconButton, Tooltip } from '@mui/material';
import _ from 'lodash';

import { ReactComponent as ChevronRightIcon } from '../../../../common/assets/icons/chevron_right_icon.svg';
import { ReactComponent as WarningIcon } from '../../../../common/assets/icons/warning_icon_red.svg';
import { SelectableCode } from '../../../../common/types/Codes';

type Props = {
  code: SelectableCode;
  showModifiersAndMultipliers: boolean;
};

const AdminSearchResultCell: React.FC<Props> = ({
  code,
  showModifiersAndMultipliers,
}) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const handleRotate = () => setIsOpen(!isOpen);
  const rotate = isOpen ? 'rotate(90deg)' : 'rotate(0)';

  return (
    <Box
      style={{
        padding: '8px',
        marginBottom: '16px',
        borderBottomWidth: '1px',
        borderBottomColor: 'rgba(82, 86, 98, 0.15)',
        borderBottomStyle: 'solid',
        borderRadius: '8px',
        backgroundColor: code.isPrimaryCode ? '#EAF6F6' : 'white',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
        }}
      >
        <IconButton
          style={{
            transform: rotate,
            transition: 'all 0.25s ease-in-out',
            alignSelf: 'flex-start',
          }}
          onClick={() => handleRotate()}
          className={`${isOpen ? 'icon-selected' : 'icon'}`}
        >
          <ChevronRightIcon />
        </IconButton>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between',
            alignItems: 'center',
            flex: 1,
            flexShrink: 1,
          }}
        >
          <Box>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
              }}
            >
              <Typography
                fontFamily={'SFProDisplay-Bold'}
                color='primary'
                sx={{
                  marginLeft: '8px',
                  marginRight: '6px',
                  cursor: 'pointer',
                }}
                onClick={() => handleRotate()}
              >
                {code.code}
              </Typography>
              {code.isFlagged && (
                <Tooltip title='Flagged' arrow>
                  <WarningIcon fillOpacity={1} />
                </Tooltip>
              )}
              <Typography
                color='secondary'
                fontSize={14}
                sx={{ paddingTop: '2px', marginLeft: '6px' }}
              >
                {code.description}
              </Typography>
            </Box>
          </Box>
          {showModifiersAndMultipliers && (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                flexShrink: 0,
              }}
            >
              <Typography
                sx={{
                  flexDirection: 'row',
                  display: 'flex',
                  whiteSpace: 'break-spaces',
                  marginRight: '8px',
                }}
                fontSize={14}
                noWrap
                color='secondary'
              >
                (Modifier -{' '}
                {_.isEmpty(code.modifiers) ? (
                  'None;'
                ) : (
                  <Typography noWrap color='primary.main'>
                    {code.modifiers.join('; ')}
                  </Typography>
                )}
              </Typography>

              <Typography noWrap fontSize={14} color='secondary'>
                Units - {code.multiplier ?? 0})
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
      {isOpen && (
        <Box sx={{ mt: '20px', ml: '33px' }} className='row'>
          <Box className='row'>
            {code.work_rvu && (
              <Box className='row' sx={{ marginRight: '35px' }}>
                <Typography
                  sx={{ marginRight: '8px' }}
                  fontFamily={'SFProDisplay-Bold'}
                >
                  {code.work_rvu}
                </Typography>
                <Typography
                  color='text.secondary'
                  fontFamily={'SFProDisplay-Regular'}
                >
                  Work RVU
                </Typography>
              </Box>
            )}
            {code.facility_total && (
              <Box className='row'>
                <Typography
                  sx={{ marginRight: '8px' }}
                  fontFamily={'SFProDisplay-Bold'}
                >
                  {code.facility_total}
                </Typography>
                <Typography
                  color='text.secondary'
                  fontFamily={'SFProDisplay-Regular'}
                >
                  Total RVU
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default AdminSearchResultCell;
